import type { SnapshotBehaviourId, SnapshotDrillId } from "@healthos/shared";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import {
  ArrowRight,
  ArrowsHorizontal,
  CaretDown,
  CaretUp,
  CircleDashed,
} from "@phosphor-icons/react";
import { cn } from "../lib/cn";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";
import { BehaviourIcon } from "./snapshot/behaviour-icon";
import { BehaviourSpark } from "./snapshot/behaviour-spark";
import { renderInlineMarkdown } from "./snapshot/inline-markdown";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot/snapshot-tokens";

/** The Snapshot digest on `/today`: */

export type SnapshotDigestTone = "calm" | "brand" | "warm" | "quiet";

export type SnapshotDigestTrend = "up" | "flat" | "down";

export type SnapshotStanding =
  | "strong"
  | "steady"
  | "holding"
  | "lifting"
  | "watch"
  | "quiet"
  | "gathering";

export interface SnapshotDigestRow {
  behaviour: SnapshotBehaviourId;
  label: string;
  /** One plain line for the week. */
  line: string;
  /** Where it stands, in the shared vocabulary: Lifting, Holding, Watch. */
  status: string;
  /** The canonical word behind it, which the chip spells out on hover. */
  standing: SnapshotStanding;
  /** Which way its own series is going, which picks the mark. */
  trend: SnapshotDigestTrend;
  tone: SnapshotDigestTone;
  /** The shape it has been making, oldest first. Null is a day with no reading. */
  spark: ReadonlyArray<number | null>;
  /** Nothing has reported yet, so the row is waiting rather than reading. */
  gathering: boolean;
  /** The level this behaviour is practising, when it has one. */
  level: number | null;
  /** True when the member's current goal is on this behaviour. */
  goal: boolean;
  /** The readings behind it, when there is a tab to open. */
  drill: SnapshotDrillId | null;
}

export interface SnapshotDigestProps {
  /** The week in one sentence, from the member's own readings. */
  headline: string;
  /** What the card wants the member to take from it, when there is something. */
  say?: string | null;
  rows: ReadonlyArray<SnapshotDigestRow>;
  /** The behaviours the member's own scores and goal are pointing at. */
  focus?: ReadonlyArray<string>;
  /** The span the lanes cover, stated once rather than left to a hover. */
  window?: string;
  onOpen: () => void;
  /** Opens one behaviour's readings. Rows are inert without it. */
  onOpenRow?: (drill: SnapshotDrillId) => void;
  className?: string;
}

/** Where a behaviour stands, as a mark rather than a word. */
/** Where the behaviour stands, as the chip the rest of the system writes state in rather than a column of loose uppercase prose. */
const TREND_ICON: Record<SnapshotDigestTrend, PhosphorIcon> = {
  up: CaretUp,
  flat: ArrowsHorizontal,
  down: CaretDown,
};

function StatusChip({
  standing,
  trend,
  tone,
  word,
}: {
  standing: SnapshotStanding;
  trend: SnapshotDigestTrend;
  tone: SnapshotDigestTone;
  word: string;
}) {
  // Nothing measured has no direction to point in; everything else takes the
  // direction of its own line rather than the band its score falls in.
  const Icon = standing === "gathering" || standing === "quiet" ? CircleDashed : TREND_ICON[trend];
  return (
    <span className={`snap-dg-status is-${tone}`}>
      <Icon size={10} weight="bold" aria-hidden="true" />
      {/* The word is the chip's accessible name at every width: it is the mark
          that collapses to a circle at rest, not the meaning. */}
      <i>{word}</i>
    </span>
  );
}

export function SnapshotDigest({
  headline,
  say = null,
  rows,
  focus = [],
  window = "Last 7 days",
  onOpen,
  onOpenRow,
  className,
}: SnapshotDigestProps) {
  return (
    <section className={cn("snap-dg", className)} aria-labelledby="snap-dg-head">
      <div className="snap-dg-top">
        <EyebrowBullet variant="plain">How we see you now</EyebrowBullet>
      </div>
      <h2 className="snap-dg-headline" id="snap-dg-head">
        {renderInlineMarkdown(headline)}
      </h2>
      {say ? <p className="snap-dg-say">{renderInlineMarkdown(say)}</p> : null}
      <ul className="snap-dg-list">
        {rows.map((row) => {
          const meta = SNAPSHOT_BEHAVIOURS.find((b) => b.id === row.behaviour);
          const open =
            row.drill && onOpenRow ? () => onOpenRow(row.drill as SnapshotDrillId) : null;
          const Row = open ? "button" : "div";
          return (
            <li
              key={row.behaviour}
              className={`snap-dg-row${row.gathering ? " is-quiet" : ""}${open ? "" : " is-nowhere"}`}
            >
              <Row {...(open ? { type: "button" as const, onClick: open } : {})}>
                {/* The icon is a mark, not a badge: five tinted pucks down the
                    left turn a read into a legend. */}
                <span className="snap-dg-icon" style={{ color: meta?.color }}>
                  {meta ? <BehaviourIcon name={meta.iconName} size={16} weight="regular" /> : null}
                </span>
                <span className="snap-dg-copy">
                  <b>
                    {row.label}
                    {/* The level sits with the name it belongs to, not in a
                        column of its own: it is a property of the behaviour,
                        the way the goal tag is. */}
                    {row.level === null ? null : (
                      <i className="snap-dg-lv" style={{ ["--accent" as string]: meta?.color }}>
                        Lv {row.level}
                      </i>
                    )}
                    {row.goal ? <em className="snap-dg-tag">goal</em> : null}
                  </b>
                  <i>{renderInlineMarkdown(row.line)}</i>
                </span>
                {/* A behaviour with no series of its own draws nothing. The
                    empty lane reads as "still waiting for its first reading",
                    which is a promise for a row that has no chart to fill. */}
                {row.spark.length > 0 ? (
                  <BehaviourSpark
                    values={row.spark}
                    accent={meta?.color}
                    gathering={row.gathering}
                    className="snap-dg-spark"
                  />
                ) : (
                  <span className="snap-dg-spark" aria-hidden="true" />
                )}
                <StatusChip
                  standing={row.standing}
                  trend={row.trend}
                  tone={row.tone}
                  word={row.status}
                />
              </Row>
            </li>
          );
        })}
      </ul>
      <div className="snap-dg-foot">
        {/* The lanes are a week wide and nothing on the card said so, which is
            answerable from a short line only by guessing. */}
        <span className="snap-dg-focus">
          {window}
          {focus.length > 0 ? ` · Your focus: ${focus.join(", ")}` : ""}
        </span>
        <button type="button" className="snap-dg-cta" onClick={onOpen}>
          View snapshot
          <ArrowRight size={13} weight="bold" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
